export type CanonicalTreeWorldCanvasMode =
  | 'readable'
  | 'overview';

export type CanonicalTreeWorldCanvasDirection =
  | 'left'
  | 'right'
  | 'up'
  | 'down';

export type CanonicalTreeOverviewFitDisposition =
  | 'COMPLETE-GRAPH-FITS'
  | 'MINIMUM-SCALE-PREVENTS-COMPLETE-FIT'
  | 'NOT-OVERVIEW';

export interface CanonicalTreeWorldBounds {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
}

export interface CanonicalTreeWorldPoint {
  readonly x: number;
  readonly y: number;
}

export interface CanonicalTreeWorldCanvasInput {
  readonly mode: CanonicalTreeWorldCanvasMode;
  readonly bounds: CanonicalTreeWorldBounds;
  readonly viewportWidth: number;
  readonly viewportHeight: number;
  readonly viewerPoint: CanonicalTreeWorldPoint;
  readonly currentFocusPoint: CanonicalTreeWorldPoint;
  readonly currentZoom: number;
  readonly readableZoom: number;
  readonly overviewMinimumZoom: number;
  readonly maximumZoom: number;
  readonly padding: number;
}

export interface CanonicalTreeWorldCanvasResult {
  readonly mode: CanonicalTreeWorldCanvasMode;
  readonly zoom: number;
  readonly focus: CanonicalTreeWorldPoint;
  readonly visibleWorldBounds: CanonicalTreeWorldBounds;
  readonly currentNavigableDirections:
    readonly CanonicalTreeWorldCanvasDirection[];
  readonly framedNavigableDirections:
    readonly CanonicalTreeWorldCanvasDirection[];
  readonly completeGraphFitsViewport: boolean;
  readonly overviewFitDisposition:
    CanonicalTreeOverviewFitDisposition;
  readonly requiredCompleteFitZoom: number;
  readonly preservesCompleteGraph: true;
  readonly mutatesGeometry: false;
}

const TOLERANCE = 0.001;

const finite = (
  value: number,
  name: string,
): number => {
  if (!Number.isFinite(value)) {
    throw new Error(
      `Invalid world-canvas value: ${name}.`,
    );
  }

  return value;
};

const positive = (
  value: number,
  name: string,
): number => {
  const checked = finite(value, name);

  if (checked <= 0) {
    throw new Error(
      `World-canvas value ${name} must be positive.`,
    );
  }

  return checked;
};

const nonNegative = (
  value: number,
  name: string,
): number => {
  const checked = finite(value, name);

  if (checked < 0) {
    throw new Error(
      `World-canvas value ${name} must be non-negative.`,
    );
  }

  return checked;
};

const freezePoint = (
  point: CanonicalTreeWorldPoint,
  name: string,
): CanonicalTreeWorldPoint =>
  Object.freeze({
    x: finite(point.x, `${name}.x`),
    y: finite(point.y, `${name}.y`),
  });

const visibleBoundsFor = (
  focus: CanonicalTreeWorldPoint,
  zoom: number,
  viewportWidth: number,
  viewportHeight: number,
  padding: number,
): CanonicalTreeWorldBounds => {
  const availableWidth =
    Math.max(viewportWidth - padding * 2, 1);

  const availableHeight =
    Math.max(viewportHeight - padding * 2, 1);

  const halfWidth =
    availableWidth / zoom / 2;

  const halfHeight =
    availableHeight / zoom / 2;

  return Object.freeze({
    minX: focus.x - halfWidth,
    maxX: focus.x + halfWidth,
    minY: focus.y - halfHeight,
    maxY: focus.y + halfHeight,
  });
};

const directionsFor = (
  world: CanonicalTreeWorldBounds,
  visible: CanonicalTreeWorldBounds,
): readonly CanonicalTreeWorldCanvasDirection[] => {
  const directions: CanonicalTreeWorldCanvasDirection[] = [];

  if (world.minX < visible.minX - TOLERANCE) {
    directions.push('left');
  }

  if (world.maxX > visible.maxX + TOLERANCE) {
    directions.push('right');
  }

  if (world.minY < visible.minY - TOLERANCE) {
    directions.push('up');
  }

  if (world.maxY > visible.maxY + TOLERANCE) {
    directions.push('down');
  }

  return Object.freeze(directions);
};

export const resolveCanonicalTreeWorldCanvas = (
  input: CanonicalTreeWorldCanvasInput,
): CanonicalTreeWorldCanvasResult => {
  const world = Object.freeze({
    minX: finite(input.bounds.minX, 'bounds.minX'),
    maxX: finite(input.bounds.maxX, 'bounds.maxX'),
    minY: finite(input.bounds.minY, 'bounds.minY'),
    maxY: finite(input.bounds.maxY, 'bounds.maxY'),
  });

  if (
    world.maxX < world.minX ||
    world.maxY < world.minY
  ) {
    throw new Error(
      'World-canvas bounds must be ordered.',
    );
  }

  const viewportWidth =
    positive(input.viewportWidth, 'viewportWidth');

  const viewportHeight =
    positive(input.viewportHeight, 'viewportHeight');

  const padding =
    nonNegative(input.padding, 'padding');

  const currentZoom =
    positive(input.currentZoom, 'currentZoom');

  const readableZoom =
    positive(input.readableZoom, 'readableZoom');

  const overviewMinimumZoom =
    positive(
      input.overviewMinimumZoom,
      'overviewMinimumZoom',
    );

  const maximumZoom =
    positive(input.maximumZoom, 'maximumZoom');

  if (
    readableZoom > maximumZoom ||
    overviewMinimumZoom > maximumZoom
  ) {
    throw new Error(
      'Governed minimum zoom cannot exceed maximum zoom.',
    );
  }

  const viewerPoint =
    freezePoint(input.viewerPoint, 'viewerPoint');

  const currentFocusPoint =
    freezePoint(
      input.currentFocusPoint,
      'currentFocusPoint',
    );

  const availableWidth =
    Math.max(viewportWidth - padding * 2, 1);

  const availableHeight =
    Math.max(viewportHeight - padding * 2, 1);

  const worldWidth =
    Math.max(world.maxX - world.minX, 1);

  const worldHeight =
    Math.max(world.maxY - world.minY, 1);

  const requiredCompleteFitZoom =
    Math.min(
      maximumZoom,
      availableWidth / worldWidth,
      availableHeight / worldHeight,
    );

  const focus =
    input.mode === 'readable'
      ? viewerPoint
      : Object.freeze({
          x: (world.minX + world.maxX) / 2,
          y: (world.minY + world.maxY) / 2,
        });

  const zoom =
    input.mode === 'readable'
      ? Math.min(readableZoom, maximumZoom)
      : Math.max(
          overviewMinimumZoom,
          requiredCompleteFitZoom,
        );

  const visibleWorldBounds =
    visibleBoundsFor(
      focus,
      zoom,
      viewportWidth,
      viewportHeight,
      padding,
    );

  const completeGraphFitsViewport =
    world.minX >= visibleWorldBounds.minX - TOLERANCE &&
    world.maxX <= visibleWorldBounds.maxX + TOLERANCE &&
    world.minY >= visibleWorldBounds.minY - TOLERANCE &&
    world.maxY <= visibleWorldBounds.maxY + TOLERANCE;

  const currentVisibleWorldBounds =
    visibleBoundsFor(
      currentFocusPoint,
      currentZoom,
      viewportWidth,
      viewportHeight,
      padding,
    );

  return Object.freeze({
    mode: input.mode,
    zoom,
    focus,
    visibleWorldBounds,
    currentNavigableDirections:
      directionsFor(
        world,
        currentVisibleWorldBounds,
      ),
    framedNavigableDirections:
      directionsFor(
        world,
        visibleWorldBounds,
      ),
    completeGraphFitsViewport,
    overviewFitDisposition:
      input.mode !== 'overview'
        ? 'NOT-OVERVIEW'
        : completeGraphFitsViewport
          ? 'COMPLETE-GRAPH-FITS'
          : 'MINIMUM-SCALE-PREVENTS-COMPLETE-FIT',
    requiredCompleteFitZoom,
    preservesCompleteGraph: true,
    mutatesGeometry: false,
  });
};


/*
 * CC-TREE-LIVE-003G3V
 *
 * Interaction projection is presentation authority only.
 *
 * Genealogy geometry remains in canonical world space and may scale.
 * Action targets and authoring panels are projected into viewport-local
 * CSS pixels and never inherit world scale.
 */

export type CanonicalTreeInteractionPanelPlacement =
  | 'below'
  | 'above';

export interface CanonicalTreeInteractionSurfaceProjectionInput {
  readonly worldPoint: CanonicalTreeWorldPoint;
  readonly pan: CanonicalTreeWorldPoint;
  readonly zoom: number;
  readonly viewportWidth: number;
  readonly viewportHeight: number;
  readonly cardWidth: number;
  readonly cardHeight: number;
  readonly actionTargetSize: number;
  readonly panelWidth: number;
  readonly panelHeight: number;
  readonly panelGap: number;
  readonly viewportMargin: number;
}

export interface CanonicalTreeInteractionSurfaceProjectionResult {
  readonly screenSpace: true;
  readonly scalesWithWorld: false;
  readonly cardCenter: CanonicalTreeWorldPoint;
  readonly addControlCenter: CanonicalTreeWorldPoint;
  readonly actionTargetSize: number;
  readonly panelWidth: number;
  readonly panelHeight: number;
  readonly panelTopLeft: CanonicalTreeWorldPoint;
  readonly panelPlacement:
    CanonicalTreeInteractionPanelPlacement;
  readonly targetVisible: boolean;
  readonly mutatesGeometry: false;
  readonly mutatesTopology: false;
  readonly grantsMutationAuthority: false;
}

const clampInteractionValue = (
  value: number,
  minimum: number,
  maximum: number,
): number =>
  Math.min(
    Math.max(value, minimum),
    maximum,
  );

export const resolveCanonicalTreeInteractionSurfaceProjection = (
  input: CanonicalTreeInteractionSurfaceProjectionInput,
): CanonicalTreeInteractionSurfaceProjectionResult => {
  const worldPoint =
    freezePoint(
      input.worldPoint,
      'interaction.worldPoint',
    );

  const pan =
    freezePoint(
      input.pan,
      'interaction.pan',
    );

  const zoom =
    positive(
      input.zoom,
      'interaction.zoom',
    );

  const viewportWidth =
    positive(
      input.viewportWidth,
      'interaction.viewportWidth',
    );

  const viewportHeight =
    positive(
      input.viewportHeight,
      'interaction.viewportHeight',
    );

  const cardWidth =
    positive(
      input.cardWidth,
      'interaction.cardWidth',
    );

  const cardHeight =
    positive(
      input.cardHeight,
      'interaction.cardHeight',
    );

  const requestedActionTargetSize =
    positive(
      input.actionTargetSize,
      'interaction.actionTargetSize',
    );

  const requestedPanelWidth =
    positive(
      input.panelWidth,
      'interaction.panelWidth',
    );

  const requestedPanelHeight =
    positive(
      input.panelHeight,
      'interaction.panelHeight',
    );

  const panelGap =
    nonNegative(
      input.panelGap,
      'interaction.panelGap',
    );

  const viewportMargin =
    nonNegative(
      input.viewportMargin,
      'interaction.viewportMargin',
    );

  if (
    viewportMargin * 2 >= viewportWidth ||
    viewportMargin * 2 >= viewportHeight
  ) {
    throw new Error(
      'Interaction viewport margin leaves no usable viewport.',
    );
  }

  /*
   * 44 CSS px is the governed minimum target.
   * This value is deliberately NOT multiplied by zoom.
   */
  const actionTargetSize =
    Math.max(
      44,
      requestedActionTargetSize,
    );

  const availablePanelWidth =
    viewportWidth -
    viewportMargin * 2;

  const availablePanelHeight =
    viewportHeight -
    viewportMargin * 2;

  const panelWidth =
    Math.min(
      requestedPanelWidth,
      availablePanelWidth,
    );

  const panelHeight =
    Math.min(
      requestedPanelHeight,
      availablePanelHeight,
    );

  /*
   * Same transformation used by FamilyTreeView.worldTransform:
   *
   * screen =
   *   viewport-center
   *   + pan
   *   + world * zoom
   */
  const cardCenter =
    Object.freeze({
      x:
        viewportWidth / 2 +
        pan.x +
        worldPoint.x * zoom,
      y:
        viewportHeight / 2 +
        pan.y +
        worldPoint.y * zoom,
    });

  /*
   * Preserve the historical lower-right add-control anchor,
   * but project only its POSITION through world scale.
   * Its interaction dimensions remain screen-space CSS px.
   */
  const addControlCenter =
    Object.freeze({
      x:
        cardCenter.x +
        (cardWidth / 2) * zoom,
      y:
        cardCenter.y +
        (cardHeight / 2) * zoom,
    });

  const halfTarget =
    actionTargetSize / 2;

  const targetVisible =
    addControlCenter.x - halfTarget >= 0 &&
    addControlCenter.x + halfTarget <=
      viewportWidth &&
    addControlCenter.y - halfTarget >= 0 &&
    addControlCenter.y + halfTarget <=
      viewportHeight;

  const belowTop =
    addControlCenter.y +
    panelGap;

  const aboveTop =
    addControlCenter.y -
    panelGap -
    panelHeight;

  const belowFits =
    belowTop +
      panelHeight <=
    viewportHeight -
      viewportMargin;

  const aboveFits =
    aboveTop >=
    viewportMargin;

  const spaceBelow =
    viewportHeight -
    viewportMargin -
    addControlCenter.y -
    panelGap;

  const spaceAbove =
    addControlCenter.y -
    panelGap -
    viewportMargin;

  const panelPlacement:
    CanonicalTreeInteractionPanelPlacement =
      belowFits
        ? 'below'
        : aboveFits
          ? 'above'
          : spaceBelow >= spaceAbove
            ? 'below'
            : 'above';

  const requestedTop =
    panelPlacement === 'below'
      ? belowTop
      : aboveTop;

  const panelLeft =
    clampInteractionValue(
      addControlCenter.x -
        panelWidth / 2,
      viewportMargin,
      viewportWidth -
        viewportMargin -
        panelWidth,
    );

  const panelTop =
    clampInteractionValue(
      requestedTop,
      viewportMargin,
      viewportHeight -
        viewportMargin -
        panelHeight,
    );

  return Object.freeze({
    screenSpace: true,
    scalesWithWorld: false,
    cardCenter,
    addControlCenter,
    actionTargetSize,
    panelWidth,
    panelHeight,
    panelTopLeft:
      Object.freeze({
        x: panelLeft,
        y: panelTop,
      }),
    panelPlacement,
    targetVisible,
    mutatesGeometry: false,
    mutatesTopology: false,
    grantsMutationAuthority: false,
  });
};
